/* ═══════════════════════════════════════════════════════════════════════════════
   WATRLY WATER BANNERS 🌊
   بانرات مائية مذهلة مع رسوم متحركة — Water Banner Components
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// Wave Animation SVG — رسوم متحركة للموجات
// ═══════════════════════════════════════════════════════════════════════════════
export const WaveAnimation = ({ className = '', color = 'rgba(10, 186, 181, 0.3)' }) => (
  <div className={`absolute bottom-0 left-0 right-0 overflow-hidden ${className}`}>
    <svg 
      viewBox="0 0 1200 120" 
      preserveAspectRatio="none"
      className="absolute bottom-0 w-[200%] h-full animate-wave"
    >
      <path 
        d="M0,60 C150,100 350,20 600,60 C850,100 1050,20 1200,60 L1200,120 L0,120 Z" 
        fill={color}
        opacity="0.6"
      />
    </svg>
    <svg 
      viewBox="0 0 1200 120" 
      preserveAspectRatio="none"
      className="absolute bottom-0 w-[200%] h-full animate-wave"
      style={{ animationDelay: '-2s', opacity: 0.4 }}
    >
      <path 
        d="M0,40 C200,80 400,0 600,40 C800,80 1000,0 1200,40 L1200,120 L0,120 Z" 
        fill={color}
      />
    </svg>
    <svg 
      viewBox="0 0 1200 120" 
      preserveAspectRatio="none"
      className="absolute bottom-0 w-[200%] h-full animate-wave"
      style={{ animationDelay: '-4s', opacity: 0.2 }}
    >
      <path 
        d="M0,80 C300,40 500,100 600,80 C700,60 900,100 1200,80 L1200,120 L0,120 Z" 
        fill={color}
      />
    </svg>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Floating Droplets — قطرات عائمة
// ═══════════════════════════════════════════════════════════════════════════════
export const FloatingDroplets = ({ count = 5 }) => {
  const droplets = Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${10 + (i * 80 / count)}%`,
    delay: `${i * 0.5}s`,
    size: 8 + Math.random() * 16,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {droplets.map((d) => (
        <div
          key={d.id}
          className="absolute rounded-full animate-bubble-rise"
          style={{
            left: d.left,
            bottom: '10%',
            width: d.size,
            height: d.size,
            background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8), rgba(10, 186, 181, 0.4))',
            boxShadow: '0 0 10px rgba(10, 186, 181, 0.3)',
            animationDelay: d.delay,
            animationDuration: '4s',
          }}
        />
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Hero Banner — البانر الرئيسي المائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterHeroBanner = ({ 
  children, 
  className = '',
  variant = 'primary',
  showWaves = true,
  showDroplets = true,
  glow = true 
}) => {
  const variantStyles = {
    primary: { overlay: 'from-[rgba(10,186,181,0.18)] via-[rgba(0,188,212,0.1)] to-[rgba(0,102,204,0.08)]', base: '#051624' },
    ocean:   { overlay: 'from-[rgba(0,102,204,0.22)] via-[rgba(0,188,212,0.12)] to-[rgba(10,186,181,0.06)]', base: '#040e1a' },
    fresh:   { overlay: 'from-[rgba(0,212,170,0.18)] via-[rgba(10,186,181,0.1)] to-[rgba(0,188,212,0.08)]', base: '#041810' },
    sunset:  { overlay: 'from-[rgba(10,186,181,0.16)] via-[rgba(41,182,246,0.1)] to-[rgba(0,102,204,0.12)]', base: '#05121e' },
  };
  const vs = variantStyles[variant] || variantStyles.primary;

  return (
    <div 
      className={`
        relative overflow-hidden rounded-3xl p-6 md:p-8
        bg-gradient-to-br ${vs.overlay}
        border border-[rgba(10,186,181,0.15)]
        backdrop-blur-xl
        ${glow ? 'shadow-[0_4px_32px_rgba(0,0,0,0.4),0_0_20px_rgba(10,186,181,0.1)]' : ''}
        ${className}
      `}
      style={{ backgroundColor: vs.base }}
    >
      {/* Background Glow Effect */}
      <div 
        className="absolute inset-0 opacity-50"
        style={{
          background: `
            radial-gradient(circle at 20% 30%, rgba(10, 186, 181, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(0, 188, 212, 0.1) 0%, transparent 40%)
          `
        }}
      />
      
      {/* Shine Effect */}
      <div 
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)'
        }}
      />

      {/* Floating Droplets */}
      {showDroplets && <FloatingDroplets count={6} />}

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>

      {/* Wave Animation */}
      {showWaves && (
        <WaveAnimation 
          color={variant === 'ocean' ? 'rgba(0, 102, 204, 0.3)' : 'rgba(10, 186, 181, 0.25)'} 
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Mini Banner — بانر صغير مائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterMiniBanner = ({ 
  icon: Icon,
  title,
  subtitle,
  variant = 'primary',
  className = '',
  onClick
}) => {
  const variantStyles = {
    primary: {
      base: '#051624',
      bg: 'from-[rgba(8,145,178,0.18)] to-[rgba(14,116,144,0.08)]',
      border: 'border-[rgba(8,145,178,0.2)]',
      icon: 'text-[#38bdf8]',
      glow: 'shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
    },
    ocean: {
      base: '#040e1a',
      bg: 'from-[rgba(29,78,216,0.18)] to-[rgba(30,64,175,0.08)]',
      border: 'border-[rgba(29,78,216,0.2)]',
      icon: 'text-[#93c5fd]',
      glow: 'shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
    },
    fresh: {
      base: '#041810',
      bg: 'from-[rgba(13,148,136,0.18)] to-[rgba(15,118,110,0.08)]',
      border: 'border-[rgba(13,148,136,0.2)]',
      icon: 'text-[#5eead4]',
      glow: 'shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
    },
    wave: {
      base: '#04121c',
      bg: 'from-[rgba(56,189,248,0.16)] to-[rgba(14,165,233,0.08)]',
      border: 'border-[rgba(56,189,248,0.2)]',
      icon: 'text-[#7dd3fc]',
      glow: 'shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
    }
  };

  const style = variantStyles[variant] || variantStyles.primary;

  return (
    <div 
      onClick={onClick}
      style={{ backgroundColor: style.base }}
      className={`
        relative overflow-hidden rounded-2xl p-4
        bg-gradient-to-br ${style.bg}
        border ${style.border}
        backdrop-blur-lg
        ${style.glow}
        transition-all duration-300 ease-out
        hover:scale-[1.02] hover:shadow-xl
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
    >
      {/* Shine */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 60%)'
        }}
      />

      <div className="relative z-10 flex items-center gap-3">
        {Icon && (
          <div className={`
            flex-shrink-0 w-12 h-12 rounded-xl
            flex items-center justify-center
            bg-[rgba(255,255,255,0.1)]
            border border-[rgba(255,255,255,0.2)]
            ${style.icon}
          `}>
            <Icon className="w-6 h-6" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-white/90 font-bold text-sm truncate">{title}</p>
          <p className="text-white/50 text-xs truncate">{subtitle}</p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Info Banner — بانر معلومات مائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterInfoBanner = ({ 
  type = 'info',
  title,
  message,
  icon: Icon,
  className = ''
}) => {
  const typeStyles = {
    info: {
      bg: 'from-[rgba(10,186,181,0.15)] to-[rgba(0,188,212,0.08)]',
      border: 'border-[rgba(10,186,181,0.25)]',
      iconBg: 'bg-[rgba(10,186,181,0.2)]',
      iconColor: 'text-[#0ABAB5]',
      accent: '#0ABAB5'
    },
    success: {
      bg: 'from-[rgba(0,212,170,0.15)] to-[rgba(10,186,181,0.08)]',
      border: 'border-[rgba(0,212,170,0.25)]',
      iconBg: 'bg-[rgba(0,212,170,0.2)]',
      iconColor: 'text-[#00D4AA]',
      accent: '#00D4AA'
    },
    warning: {
      bg: 'from-[rgba(251,191,36,0.15)] to-[rgba(251,191,36,0.05)]',
      border: 'border-[rgba(251,191,36,0.25)]',
      iconBg: 'bg-[rgba(251,191,36,0.2)]',
      iconColor: 'text-[#fbbf24]',
      accent: '#fbbf24'
    },
    error: {
      bg: 'from-[rgba(244,63,94,0.15)] to-[rgba(244,63,94,0.05)]',
      border: 'border-[rgba(244,63,94,0.25)]',
      iconBg: 'bg-[rgba(244,63,94,0.2)]',
      iconColor: 'text-[#f43f5e]',
      accent: '#f43f5e'
    }
  };

  const style = typeStyles[type];

  return (
    <div 
      className={`
        relative overflow-hidden rounded-xl p-4
        bg-gradient-to-br ${style.bg}
        border ${style.border}
        backdrop-blur-sm
        ${className}
      `}
    >
      {/* Side accent line */}
      <div 
        className="absolute right-0 top-4 bottom-4 w-1 rounded-full"
        style={{ background: style.accent }}
      />

      <div className="flex items-start gap-3 pr-3">
        {Icon && (
          <div className={`
            flex-shrink-0 w-10 h-10 rounded-lg
            flex items-center justify-center
            ${style.iconBg} ${style.iconColor}
          `}>
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <p className="text-white font-bold text-sm mb-1">{title}</p>
          )}
          <p className="text-white/70 text-sm leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Stats Banner — بانر إحصائيات مائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterStatsBanner = ({ 
  stats = [],
  className = ''
}) => {
  return (
    <div className={`
      relative overflow-hidden rounded-2xl p-4
      bg-gradient-to-br from-[rgba(255,255,255,0.08)] to-[rgba(255,255,255,0.04)]
      border border-[rgba(255,255,255,0.15)]
      backdrop-blur-xl
      shadow-[0_8px_32px_rgba(0,0,0,0.1)]
      ${className}
    `}>
      {/* Background effect */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: `
            radial-gradient(circle at 30% 20%, rgba(10, 186, 181, 0.1) 0%, transparent 40%),
            radial-gradient(circle at 70% 80%, rgba(0, 188, 212, 0.08) 0%, transparent 40%)
          `
        }}
      />

      <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={index}
            className="text-center p-3 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.08)] transition-colors"
          >
            <p className="text-2xl md:text-3xl font-bold text-white mb-1">
              {stat.value}
            </p>
            <p className="text-white/50 text-xs">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Promo Banner — بانر ترويجي مائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterPromoBanner = ({ 
  title,
  description,
  ctaText,
  onCtaClick,
  variant = 'primary',
  className = ''
}) => {
  const variantStyles = {
    primary: {
      bg: 'from-[#0ABAB5] via-[#089C98] to-[#0066CC]',
      glow: 'shadow-[0_0_40px_rgba(10,186,181,0.4)]'
    },
    ocean: {
      bg: 'from-[#0066CC] via-[#004499] to-[#002952]',
      glow: 'shadow-[0_0_40px_rgba(0,102,204,0.4)]'
    },
    fresh: {
      bg: 'from-[#00D4AA] via-[#00A884] to-[#0ABAB5]',
      glow: 'shadow-[0_0_40px_rgba(0,212,170,0.4)]'
    }
  };

  const style = variantStyles[variant];

  return (
    <div 
      className={`
        relative overflow-hidden rounded-2xl p-6
        bg-gradient-to-br ${style.bg}
        ${style.glow}
        ${className}
      `}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-20">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="waterPattern" patternUnits="userSpaceOnUse" width="20" height="20">
              <circle cx="10" cy="10" r="1" fill="white" opacity="0.3" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#waterPattern)" />
        </svg>
      </div>

      {/* Shine effect */}
      <div 
        className="absolute -top-20 -right-20 w-60 h-60 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)'
        }}
      />

      <div className="relative z-10">
        <h3 className="text-white font-bold text-xl mb-2">{title}</h3>
        <p className="text-white/80 text-sm mb-4 leading-relaxed">{description}</p>
        {ctaText && (
          <button
            onClick={onCtaClick}
            className="
              px-6 py-2.5 rounded-xl
              bg-white/20 backdrop-blur-sm
              border border-white/30
              text-white font-semibold text-sm
              transition-all duration-300
              hover:bg-white/30 hover:scale-105
              active:scale-95
            "
          >
            {ctaText}
          </button>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Greeting Banner — بانر الترحيب
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterGreetingBanner = ({ 
  name,
  greeting,
  phone,
  avatar,
  className = ''
}) => {
  const hour = new Date().getHours();
  const defaultGreeting = hour < 12 ? 'صباح الخير' : hour < 17 ? 'مساء الخير' : 'مساء النور';

  return (
    <WaterHeroBanner 
      variant="sunset" 
      showWaves={true}
      showDroplets={true}
      className={className}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[#22d3ee]/80 text-sm font-semibold mb-1">
            {greeting || defaultGreeting} 👋
          </p>
          <h1 className="text-white text-2xl md:text-3xl font-bold mb-1">
            {name || 'أهلاً بك'}
          </h1>
          {phone && (
            <p className="text-white/40 text-xs font-mono" dir="ltr">
              {phone}
            </p>
          )}
        </div>

        {/* Avatar with water effect */}
        <div className="relative flex-shrink-0 ml-4">
          <div 
            className="w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden
                       bg-gradient-to-br from-[#0ABAB5] to-[#0066CC]
                       p-[2px]"
          >
            <div className="w-full h-full rounded-2xl overflow-hidden bg-[#0A1628] flex items-center justify-center">
              {avatar ? (
                <img src={avatar} alt={name} className="w-full h-full object-cover" />
              ) : (
                <svg 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                  className="w-8 h-8 md:w-10 md:h-10 text-[#0ABAB5]"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              )}
            </div>
          </div>
          
          {/* Online indicator */}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#00D4AA] border-2 border-[#0A1628]" />
        </div>
      </div>
    </WaterHeroBanner>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Active Orders Banner — بانر الطلبات النشطة (slider)
// ═══════════════════════════════════════════════════════════════════════════════
const CANCELLABLE = new Set(['pending','confirmed','no_driver_available','collecting_offers','expired_no_offers']);

export const WaterActiveOrderBanner = ({
  orders = [],        // [{ id, orderNumber, status, statusLabel, details, canCancel }]
  onOrderClick,
  onCancel,
  className = '',
}) => {
  const [idx, setIdx] = useState(0);
  const [anim, setAnim] = useState('');
  const total = orders.length;

  const go = useCallback((dir) => {
    setAnim(dir === 1 ? 'slide-l' : 'slide-r');
    setTimeout(() => {
      setIdx(i => (i + dir + total) % total);
      setAnim('');
    }, 220);
  }, [total]);

  // Auto-flip every 4s when multiple orders
  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(() => go(1), 4000);
    return () => clearInterval(t);
  }, [total, go]);

  if (total === 0) return null;
  const order = orders[idx];

  return (
    <div className={className} style={{ fontFamily: "'Cairo', sans-serif" }}>
      <style>{`
        @keyframes wt-sl      { 0%{opacity:0;transform:translateX(-18px)} 100%{opacity:1;transform:translateX(0)} }
        @keyframes wt-sr      { 0%{opacity:0;transform:translateX(18px)}  100%{opacity:1;transform:translateX(0)} }
        .wt-anim-sl { animation: wt-sl 0.22s ease }
        .wt-anim-sr { animation: wt-sr 0.22s ease }
        @keyframes wt-pulse-op { 0%,100%{opacity:0} 40%,60%{opacity:1} }
        @keyframes wt-drop {
          0%   { transform:translateY(-8px); opacity:0; }
          15%  { opacity:0.9; }
          85%  { opacity:0.6; }
          100% { transform:translateY(52px); opacity:0; }
        }
      `}</style>

      {/* Count bar — only when >1 */}
      {total > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '0.4rem', padding: '0 0.25rem',
        }}>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700,
            color: 'var(--wt-text-muted)',
          }}>
            الطلبات النشطة
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            fontSize: '0.72rem', fontWeight: 800,
            padding: '0.15rem 0.55rem', borderRadius: 999,
            background: 'rgba(10,186,181,0.12)',
            border: '1px solid rgba(10,186,181,0.3)',
            color: '#0ABAB5',
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: '#0ABAB5', display: 'inline-block',
              animation: 'wt-gp 1.4s ease-in-out infinite',
            }} />
            {total} طلب نشط
          </span>
          <style>{`@keyframes wt-gp{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
        </div>
      )}

      {/* Card — no overflow:hidden to avoid clipping-triggered repaints */}
      <div
        onClick={() => onOrderClick?.(order.id)}
        style={{
          position: 'relative',
          borderRadius: '0.875rem', cursor: 'pointer',
          padding: '0.6rem 0.75rem',
          background: 'var(--wt-surface)',
          border: '1px solid rgba(10,186,181,0.35)',
          boxShadow: '0 2px 16px rgba(10,186,181,0.12)',
        }}
      >
        {/* Inner clip wrapper — isolated layer for decorations only */}
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '0.875rem',
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}>
          {/* Ripple 1 — opacity only, no transform */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 80% 100% at 50% 50%, rgba(5,140,135,0.3) 0%, transparent 70%)',
            animation: 'wt-pulse-op 3s ease-in-out infinite',
            willChange: 'opacity',
          }} />
          {/* Ripple 2 — offset */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 70% 90% at 50% 50%, rgba(0,80,160,0.2) 0%, transparent 70%)',
            animation: 'wt-pulse-op 3s ease-in-out 1.5s infinite',
            willChange: 'opacity',
          }} />
          {/* Falling drops — translateY على عناصر position:absolute لا يحرك layout */}
          {[
            { left: '8%',  delay: '0s',    dur: '2.2s', size: 5, op: 0.55 },
            { left: '20%', delay: '0.7s',  dur: '2.8s', size: 4, op: 0.4  },
            { left: '35%', delay: '1.4s',  dur: '2.0s', size: 6, op: 0.6  },
            { left: '52%', delay: '0.3s',  dur: '2.5s', size: 4, op: 0.35 },
            { left: '67%', delay: '1.1s',  dur: '2.3s', size: 5, op: 0.5  },
            { left: '80%', delay: '0.5s',  dur: '2.7s', size: 4, op: 0.4  },
            { left: '91%', delay: '1.8s',  dur: '2.1s', size: 5, op: 0.45 },
          ].map((d, i) => (
            <svg
              key={i}
              viewBox="0 0 10 13"
              style={{
                position: 'absolute',
                top: 0,
                left: d.left,
                width: d.size,
                height: d.size * 1.3,
                opacity: d.op,
                animation: `wt-drop ${d.dur} ease-in ${d.delay} infinite`,
                willChange: 'transform, opacity',
              }}
            >
              <path
                d="M5 0 C3 3 0 5.5 0 8 A5 5 0 0 0 10 8 C10 5.5 7 3 5 0Z"
                fill="rgba(10,186,181,0.9)"
              />
              <ellipse cx="6.5" cy="6" rx="1.2" ry="1.8"
                fill="rgba(255,255,255,0.35)" transform="rotate(-20 6.5 6)"/>
            </svg>
          ))}
        </div>

        <div
          className={anim === 'slide-l' ? 'wt-anim-sl' : anim === 'slide-r' ? 'wt-anim-sr' : ''}
          style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', position: 'relative', zIndex: 1 }}
        >
          {/* Icon */}
          <div style={{
            width: 34, height: 34, borderRadius: '0.6rem', flexShrink: 0,
            background: 'linear-gradient(135deg, #075985, #0891b2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 14px rgba(6,182,212,0.35)',
            overflow: 'hidden',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7"/>
              <path d="M16.5 16.5L21 21"/>
            </svg>
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.15rem' }}>
              <span style={{
                fontSize: '0.68rem', fontWeight: 800, padding: '0.1rem 0.45rem',
                borderRadius: 999,
                background: 'rgba(10,186,181,0.15)',
                border: '1px solid rgba(10,186,181,0.3)',
                color: '#0ABAB5',
              }}>
                {order.statusLabel}
              </span>
            </div>
            <p style={{
              margin: 0, fontWeight: 800, fontSize: '0.85rem',
              color: 'var(--wt-text-primary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }} dir="ltr">
              {order.orderNumber}
            </p>
            <p style={{
              margin: '0.08rem 0 0', fontSize: '0.71rem',
              color: 'var(--wt-text-muted)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {order.details}
            </p>
          </div>

          {/* Cancel button — only for cancellable statuses */}
          {CANCELLABLE.has(order.status) && onCancel && (
            <button
              onClick={e => { e.stopPropagation(); onCancel(order.id); }}
              style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.3rem 0.6rem', borderRadius: '0.6rem',
                background: 'linear-gradient(135deg,#f43f5e,#e11d48)',
                border: 'none', cursor: 'pointer',
                color: '#fff', fontSize: '0.65rem', fontWeight: 800,
                boxShadow: '0 2px 10px rgba(244,63,94,0.4)',
                fontFamily: "'Cairo',sans-serif",
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                <circle cx="12" cy="12" r="9"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
              إلغاء
            </button>
          )}

          {/* Nav arrows (only when >1) */}
          {total > 1 && (
            <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
              <button
                onClick={e => { e.stopPropagation(); go(-1); }}
                style={{
                  width: 28, height: 28, borderRadius: '0.5rem', border: '1px solid var(--wt-border)',
                  background: 'var(--wt-bg-2)', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ color: 'var(--wt-text-muted)' }}>
                  <path strokeLinecap="round" d="M9 5l7 7-7 7"/>
                </svg>
              </button>
              <button
                onClick={e => { e.stopPropagation(); go(1); }}
                style={{
                  width: 28, height: 28, borderRadius: '0.5rem', border: '1px solid var(--wt-border)',
                  background: 'var(--wt-bg-2)', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ color: 'var(--wt-text-muted)' }}>
                  <path strokeLinecap="round" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
            </div>
          )}

          {/* Single order arrow */}
          {total === 1 && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ color: 'var(--wt-text-muted)', flexShrink: 0 }}>
              <path strokeLinecap="round" d="M15 19l-7-7 7-7"/>
            </svg>
          )}
        </div>

        {/* Dots */}
        {total > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
            {orders.map((_, i) => (
              <div
                key={i}
                onClick={e => { e.stopPropagation(); setIdx(i); }}
                style={{
                  width: i === idx ? 16 : 6, height: 6, borderRadius: 99, cursor: 'pointer',
                  background: i === idx ? '#0ABAB5' : 'var(--wt-border)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default {
  WaveAnimation,
  FloatingDroplets,
  WaterHeroBanner,
  WaterMiniBanner,
  WaterInfoBanner,
  WaterStatsBanner,
  WaterPromoBanner,
  WaterGreetingBanner,
  WaterActiveOrderBanner,
};
